import * as React from "react";
import {
  BorrowerNotificationPermissions,
  BorrowerNotificationSettings,
} from "./LinkedAccountsPage";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  ToggleButtonGroupProps,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import {
  Control,
  Controller,
  FieldValues,
  Path,
  PathValue,
  useFormContext,
} from "react-hook-form";
import { useLanguage } from "../../Language";
import { BorrowerNotificationType } from "@tlc/ls2pac/dto/BorrowerNotificationType";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useBorrowerNotificationBranchConfig } from "../obr/useBorrowerNotificationBranchConfig";

export type PatronNotificationTypes =
  | "arrivedHoldNotificationTypes"
  | "automaticRenewalNotificationTypes"
  | "cancelledHoldNotificationTypes"
  | "overdueNotificationTypes"
  | "expiredCardNotificationTypes"
  | "finalOverdueNotificationTypes"
  | "friendlyWarningNotificationTypes";

// These keys also define the order
export const NOTIFICATION_KEYS: PatronNotificationTypes[] = [
  "expiredCardNotificationTypes",
  "automaticRenewalNotificationTypes",
  "arrivedHoldNotificationTypes",
  "cancelledHoldNotificationTypes",
  "friendlyWarningNotificationTypes",
  "overdueNotificationTypes",
  "finalOverdueNotificationTypes",
];

const TRANSLATE_KEYS: Record<PatronNotificationTypes, string> = {
  arrivedHoldNotificationTypes: "ci_notifyArrivedHold",
  automaticRenewalNotificationTypes: "ci_notifyAutomaticRenewal",
  cancelledHoldNotificationTypes: "ci_notifyCancelledHold",
  overdueNotificationTypes: "ci_notifyOverdue",
  expiredCardNotificationTypes: "ci_notifyAccountExpiration",
  finalOverdueNotificationTypes: "ci_notifyFinalOverdue",
  friendlyWarningNotificationTypes: "ci_notifyFriendlyWarning",
};

const PERMISSION_KEYS: Record<
  PatronNotificationTypes,
  keyof BorrowerNotificationPermissions
> = {
  arrivedHoldNotificationTypes: "canBorChangeArrivedHold",
  automaticRenewalNotificationTypes: "canBorChangeAutoRenewal",
  cancelledHoldNotificationTypes: "canBorChangeCanceledHld",
  overdueNotificationTypes: "canBorChangeOverdue",
  expiredCardNotificationTypes: "canBorChangeAccntExpire",
  finalOverdueNotificationTypes: "canBorChangeFinalOverdue",
  friendlyWarningNotificationTypes: "canBorChangeFriendlyWarn",
};

const ALLOW_NONE: Record<PatronNotificationTypes, boolean> = {
  arrivedHoldNotificationTypes: false,
  automaticRenewalNotificationTypes: true,
  cancelledHoldNotificationTypes: true,
  overdueNotificationTypes: false,
  expiredCardNotificationTypes: true,
  finalOverdueNotificationTypes: false,
  friendlyWarningNotificationTypes: true,
};
interface BorrowerNotificationBranchConfig {
  emailAddress1Label: string;
  emailAddress2Label: string;
  emailAddress3Label: string;
  fromEmailAddress: string;
  usingThirdParty: boolean;
  thirdPartyProviderName: string;
  /**
   * Default selections
   */
  settings: BorrowerNotificationSettings;
  permissions: BorrowerNotificationPermissions;
}
export interface GetBorrowerNotificationBranchConfigResponse {
  borrowerNotificationTypesAvailable: BorrowerNotificationSettings;
  borrowerNotificationBranchConfig: BorrowerNotificationBranchConfig;
}

interface CustomizeNotificationsToggleProps {
  value: boolean;
  onChange: (event: React.MouseEvent<HTMLElement>, newValue: boolean) => void;
}
const CustomizeNotificationsToggle: React.FC<
  CustomizeNotificationsToggleProps
> = ({ value, onChange }) => {
  const { translate } = useLanguage();
  return (
    <ToggleButtonGroup
      value={value}
      exclusive
      color="secondary"
      onChange={(event, newValue) => {
        if (newValue === null) {
          // don't allow deselection
          return;
        }
        onChange(event, newValue);
      }}
    >
      <ToggleButton value={false}>
        <Stack direction="row" spacing={{ xs: 1 }} useFlexGap>
          {!value && <CheckIcon />}
          {translate("useDefaults")}
        </Stack>
      </ToggleButton>
      <ToggleButton value={true}>
        <Stack direction="row" spacing={{ xs: 1 }} useFlexGap>
          {value && <CheckIcon />}
          {translate("customizeOptions")}
        </Stack>
      </ToggleButton>
    </ToggleButtonGroup>
  );
};
interface NotificationPreferenceControlProps<T extends FieldValues>
  extends ToggleButtonGroupProps {
  allowNone?: boolean;
  name: Path<T>;
  label: string;
  options: BorrowerNotificationType[];
  control: Control<T>;
}
const NotificationPreferenceControl = <T extends FieldValues>({
  name,
  label,
  control,
  options,
  allowNone = false,
  ...props
}: NotificationPreferenceControlProps<T>) => {
  const { translate } = useLanguage();
  const labels: Record<BorrowerNotificationType, string> = {
    [BorrowerNotificationType.TextMessage]: translate(
      "ci_notificationType_TextMessage"
    ),
    [BorrowerNotificationType.Email]: translate("ci_notificationType_Email"),
    [BorrowerNotificationType.Phone]: translate("ci_notificationType_Phone"),
    [BorrowerNotificationType.Paper]: translate("ci_notificationType_Paper"),
  };
  const noneValue = "None";

  const sortedOptions = React.useMemo(() => {
    const order = [
      BorrowerNotificationType.Email,
      BorrowerNotificationType.TextMessage,
      BorrowerNotificationType.Phone,
      BorrowerNotificationType.Paper,
    ];
    return options
      .filter((option) => {
        // filter out any unhandled options
        return order.includes(option);
      })
      .sort((a, b) => {
        return order.indexOf(a) - order.indexOf(b);
      });
  }, [options]);

  return (
    <FormControl>
      <FormLabel>{label}</FormLabel>
      <Controller<T>
        name={name}
        control={control}
        render={({ field }) => {
          return (
            <ToggleButtonGroup
              {...props}
              {...field}
              value={
                (field.value || []).length === 0 ? [noneValue] : field.value
              }
              color="secondary"
              onChange={(event, newValue) => {
                const fieldValue: BorrowerNotificationType[] =
                  field.value || [];
                if (!allowNone && newValue.length === 0) {
                  // Don't allow deselection if allowNone is false
                  return;
                }
                if (newValue.includes(noneValue) && fieldValue.length > 0) {
                  // if user clicked "None"
                  field.onChange([]);
                } else if (
                  newValue.includes(noneValue) &&
                  newValue.length > 1 &&
                  fieldValue.length === 0
                ) {
                  // if "None" was already selected and user makes an additional selection
                  field.onChange(
                    newValue.filter(
                      (val: BorrowerNotificationType | "None") =>
                        val !== noneValue
                    )
                  );
                } else {
                  field.onChange(newValue);
                }
              }}
            >
              {sortedOptions.map((notificationType) => (
                <ToggleButton key={notificationType} value={notificationType}>
                  <Stack direction="row" spacing={{ xs: 1 }} useFlexGap>
                    {(field.value || []).includes(notificationType) && (
                      <CheckIcon />
                    )}
                    {labels[notificationType]}
                  </Stack>
                </ToggleButton>
              ))}
              {allowNone && (
                <ToggleButton value={noneValue}>
                  <Stack direction="row" spacing={{ xs: 1 }} useFlexGap>
                    {(field.value || []).length === 0 && <CheckIcon />}
                    {translate("none")}
                  </Stack>
                </ToggleButton>
              )}
            </ToggleButtonGroup>
          );
        }}
      />
    </FormControl>
  );
};

interface NotificationPreferencesProps<T extends FieldValues> {
  expiredCardName: Path<T>;
  friendlyWarningName: Path<T>;
  overdueName: Path<T>;
  finalOverdueName: Path<T>;
  arrivedHoldName: Path<T>;
  cancelledHoldName: Path<T>;
  automaticRenewalName: Path<T>;
  issuingLocation: number;
  hidden?: boolean;
}

export const NotificationPreferences = <T extends FieldValues>({
  expiredCardName,
  friendlyWarningName,
  overdueName,
  finalOverdueName,
  arrivedHoldName,
  cancelledHoldName,
  automaticRenewalName,
  issuingLocation,
  hidden = false,
}: NotificationPreferencesProps<T>) => {
  const { translate } = useLanguage();
  const { control, setValue, getValues, watch } = useFormContext<T>();
  const [showDefaults, setShowDefaults] = React.useState<boolean>(false);
  const [customizeNotificationPrefs, setCustomizeNotificationPrefs] =
    React.useState<boolean>();
  const config = useBorrowerNotificationBranchConfig(issuingLocation);

  const isDefaultSelected = (
    selectedNotificationTypes: BorrowerNotificationType[],
    defaultNotificationTypes: BorrowerNotificationType[]
  ) => {
    return (
      JSON.stringify(defaultNotificationTypes) ===
      JSON.stringify(selectedNotificationTypes)
    );
  };

  React.useEffect(() => {
    const subscription = watch((value, { name: fieldName, type }) => {
      if (!!!fieldName && !!!type) {
        if (process.env.NODE_ENV !== "production") {
          console.log("Form has been reset");
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  React.useEffect(() => {
    if (config === undefined) {
      // has not been requested
      return;
    }
    if (config === null) {
      // has been cleared
      return;
    }

    if (customizeNotificationPrefs === undefined) {
      // get field values
      const { settings: defaults } = config.borrowerNotificationBranchConfig;
      const isCustomized = NOTIFICATION_KEYS.some((key) => {
        const value = getValues(getFieldName(key));
        const fieldName = getFieldName(key);
        if (process.env.NODE_ENV !== "production") {
          console.log(`${fieldName} value:`, value, "default", defaults[key]);
        }

        if (!value) {
          // if there is no value, set it to the default and return false
          setValue(fieldName, defaults[key] as PathValue<T, Path<T>>, {
            shouldDirty: false,
          });
          return false;
        }
        // otherwise, return if it does not match the default
        return !isDefaultSelected(value, defaults[key]);
      });
      if (process.env.NODE_ENV !== "production") {
        console.log("Derriving customizeNotificationPrefs", isCustomized);
      }
      setCustomizeNotificationPrefs(isCustomized);
    } else {
      setCustomizeNotificationPrefs(false);
      setDefaults();
    }
  }, [config]);

  const setDefaults = () => {
    if (!config) {
      return;
    }
    const { settings: defaults } = config.borrowerNotificationBranchConfig;
    NOTIFICATION_KEYS.forEach((key) => {
      console.log(`Setting default for ${getFieldName(key)}:`, defaults[key]);
      setValue(getFieldName(key), defaults[key] as PathValue<T, Path<T>>);
    });
  };

  const displayNotificationPreferences = React.useMemo(() => {
    if (hidden === true) {
      return false;
    }
    if (!config) {
      return false;
    }
    const { permissions } = config.borrowerNotificationBranchConfig;
    return NOTIFICATION_KEYS.some((key) => permissions[PERMISSION_KEYS[key]]);
  }, [config, hidden]);

  const getFieldName = (key: PatronNotificationTypes) => {
    switch (key) {
      case "arrivedHoldNotificationTypes":
        return arrivedHoldName;
      case "automaticRenewalNotificationTypes":
        return automaticRenewalName;
      case "cancelledHoldNotificationTypes":
        return cancelledHoldName;
      case "expiredCardNotificationTypes":
        return expiredCardName;
      case "finalOverdueNotificationTypes":
        return finalOverdueName;
      case "friendlyWarningNotificationTypes":
        return friendlyWarningName;
      case "overdueNotificationTypes":
        return overdueName;
    }
  };

  return (
    <>
      {displayNotificationPreferences && (
        <Box sx={{ pb: 4 }}>
          <FormControl>
            <FormLabel>{translate("ci_notificationType")}</FormLabel>
            <CustomizeNotificationsToggle
              value={customizeNotificationPrefs}
              onChange={(_, newValue) => {
                setCustomizeNotificationPrefs(newValue);
                if (newValue === false) {
                  setDefaults();
                }
              }}
            ></CustomizeNotificationsToggle>
            <FormHelperText>
              {customizeNotificationPrefs === false ? (
                showDefaults ? (
                  <Button
                    variant="text"
                    onClick={() => setShowDefaults(false)}
                    startIcon={<VisibilityOffIcon />}
                  >
                    {translate("hideDefaults")}
                  </Button>
                ) : (
                  <Button
                    variant="text"
                    onClick={() => setShowDefaults(true)}
                    startIcon={<VisibilityIcon />}
                  >
                    {translate("showDefaults")}
                  </Button>
                )
              ) : (
                " "
              )}
            </FormHelperText>
          </FormControl>
          <Stack sx={{
            gap: 2
          }}>
            {(customizeNotificationPrefs === true || showDefaults) &&
              NOTIFICATION_KEYS.map((key) => {
                if (
                  config?.borrowerNotificationBranchConfig?.permissions[
                    PERMISSION_KEYS[key]
                  ]
                ) {
                  return (
                    <NotificationPreferenceControl
                      key={key}
                      options={config?.borrowerNotificationTypesAvailable[key]}
                      disabled={!customizeNotificationPrefs}
                      name={getFieldName(key)}
                      label={translate(TRANSLATE_KEYS[key])}
                      control={control}
                      allowNone={ALLOW_NONE[key]}
                    />
                  );
                }
                return null;
              })}
          </Stack>
        </Box>
      )}
    </>
  );
};
